// App-wide context: current role, dashboard series visibility, simulation and
// settings state, and the recipe queue/active-recipe state shared between
// Setup, Dashboard and MachineControls. Most values persist to localStorage.
import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';

const AppContext = createContext(null);

export function AppContextProvider({ children }) {
  const [currentRole, setCurrentRole] = useState('admin');

  const [dashboardVisibleSeries, setDashboardVisibleSeriesState] = useState({
    "Program A": true,
    "Program B": true,
    "Program C": true,
    "Program D": true,
    "Total": true
  });
  
  const [selectedSimulation, setSelectedSimulationState] = useState("");
  const [sliderValue, setSliderValueState] = useState(0);

  const [settingsMode, setSettingsModeState] = useState("preset");
  const [assignedPrograms, setAssignedProgramsState] = useState([]);

  // Queue shared between Setup and MachineControls.
  const [assignedRecipes, setAssignedRecipesState] = useState([]);

  // Recipes currently running on gates (includes order info).
  const [activeRecipes, setActiveRecipesState] = useState([]);

  // recipeName → { orderId, customerName, requestedBatches, completedBatches, status }
  const [recipeOrderMap, setRecipeOrderMapState] = useState({});

  // Load persisted data on mount
  useEffect(() => {
    try {
      const dashboardData = localStorage.getItem('dashboard_visibleSeries');
      if (dashboardData) {
        setDashboardVisibleSeriesState(JSON.parse(dashboardData));
      }
    } catch (error) { /* ignore */ }
    
    try {
      const simulationData = localStorage.getItem('simulation_selectedSimulation');
      if (simulationData) {
        setSelectedSimulationState(simulationData);
      }
    } catch (error) { /* ignore */ }
    
    try {
      const sliderData = localStorage.getItem('simulation_sliderValue');
      if (sliderData) {
        setSliderValueState(Number(sliderData));
      }
    } catch (error) { /* ignore */ }
    
    try {
      const settingsModeData = localStorage.getItem('settings_mode');
      if (settingsModeData) {
        setSettingsModeState(settingsModeData);
      }
    } catch (error) { /* ignore */ }
    
    try {
      const assignedProgramsData = localStorage.getItem('settings_assignedPrograms');
      if (assignedProgramsData) {
        setAssignedProgramsState(JSON.parse(assignedProgramsData));
      }
    } catch (error) { /* ignore */ }
    
    // assignedRecipes (order queue) is loaded from the backend by Setup,
    // not from localStorage.
    try {
      const activeRecipesData = localStorage.getItem('activeRecipes');
      if (activeRecipesData) {
        setActiveRecipesState(JSON.parse(activeRecipesData));
      }
    } catch (error) { /* ignore */ }
    
    try {
      const recipeOrderMapData = localStorage.getItem('recipeOrderMap');
      if (recipeOrderMapData) {
        setRecipeOrderMapState(JSON.parse(recipeOrderMapData));
      }
    } catch (error) { /* ignore */ }
  }, []);

  // Stable setter wrappers — useCallback keeps references stable across renders
  const setDashboardVisibleSeries = useCallback((value) => {
    if (typeof value === 'function') {
      setDashboardVisibleSeriesState(prev => {
        const updated = value(prev);
        try { localStorage.setItem('dashboard_visibleSeries', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
    } else {
      setDashboardVisibleSeriesState(value);
      try { localStorage.setItem('dashboard_visibleSeries', JSON.stringify(value)); } catch (_) {}
    }
  }, []);
  
  const setSelectedSimulation = useCallback((value) => {
    setSelectedSimulationState(value);
    try { localStorage.setItem('simulation_selectedSimulation', value); } catch (_) {}
  }, []);
  
  const setSliderValue = useCallback((value) => {
    setSliderValueState(value);
    try { localStorage.setItem('simulation_sliderValue', String(value)); } catch (_) {}
  }, []);
  
  const setSettingsMode = useCallback((value) => {
    setSettingsModeState(value);
    try { localStorage.setItem('settings_mode', value); } catch (_) {}
  }, []);
  
  const setAssignedPrograms = useCallback((value) => {
    if (typeof value === 'function') {
      setAssignedProgramsState(prev => {
        const updated = value(prev);
        try { localStorage.setItem('settings_assignedPrograms', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
    } else {
      setAssignedProgramsState(value);
      try { localStorage.setItem('settings_assignedPrograms', JSON.stringify(value)); } catch (_) {}
    }
  }, []);
  
  const setAssignedRecipes = useCallback((value) => {
    if (typeof value === 'function') {
      setAssignedRecipesState(prev => value(prev));
    } else {
      setAssignedRecipesState(value);
    }
  }, []);
  
  const setActiveRecipes = useCallback((value) => {
    if (typeof value === 'function') {
      setActiveRecipesState(prev => {
        const updated = value(prev);
        try { localStorage.setItem('activeRecipes', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
    } else {
      setActiveRecipesState(value);
      try { localStorage.setItem('activeRecipes', JSON.stringify(value)); } catch (_) {}
    }
  }, []);
  
  const setRecipeOrderMap = useCallback((value) => {
    if (typeof value === 'function') {
      setRecipeOrderMapState(prev => {
        const updated = value(prev);
        try { localStorage.setItem('recipeOrderMap', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
    } else {
      setRecipeOrderMapState(value);
      try { localStorage.setItem('recipeOrderMap', JSON.stringify(value)); } catch (_) {}
    }
  }, []);

  const contextValue = useMemo(() => ({
    currentRole, setCurrentRole,
    dashboardVisibleSeries, setDashboardVisibleSeries,
    selectedSimulation, setSelectedSimulation,
    sliderValue, setSliderValue,
    settingsMode, setSettingsMode,
    assignedPrograms, setAssignedPrograms,
    assignedRecipes, setAssignedRecipes,
    activeRecipes, setActiveRecipes,
    recipeOrderMap, setRecipeOrderMap,
  }), [
    currentRole, dashboardVisibleSeries, selectedSimulation, sliderValue,
    settingsMode, assignedPrograms, assignedRecipes, activeRecipes, recipeOrderMap,
    setCurrentRole, setDashboardVisibleSeries, setSelectedSimulation, setSliderValue,
    setSettingsMode, setAssignedPrograms, setAssignedRecipes, setActiveRecipes, setRecipeOrderMap,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

/**
 * Returns the app context; outside a provider it returns inert defaults
 * instead of throwing, so isolated component renders don't crash.
 * @returns {object} Context value.
 */
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === null || context === undefined) {
    return {
      currentRole: 'admin',
      setCurrentRole: () => {},
      dashboardVisibleSeries: {
        "Program A": true,
        "Program B": true,
        "Program C": true,
        "Program D": true,
        "Total": true
      },
      setDashboardVisibleSeries: () => {},
      selectedSimulation: "",
      setSelectedSimulation: () => {},
      sliderValue: 0,
      setSliderValue: () => {},
      settingsMode: "preset",
      setSettingsMode: () => {},
      assignedPrograms: [],
      setAssignedPrograms: () => {},
      assignedRecipes: [],
      setAssignedRecipes: () => {},
      activeRecipes: [],
      setActiveRecipes: () => {},
      recipeOrderMap: {},
      setRecipeOrderMap: () => {},
    };
  }
  return context;
}

// Backward-compatible alias.
export { AppContextProvider as AppProvider };
